import type { Sale, SaleItem } from './mock-data'
import { UNIT_SHORT, type ProductUnit } from './mock-data'
import { isMeasure } from './weight'
import { vatInside } from './tax'
import { lariInWords } from './money-words'
import { esc, m2 } from './print'
import { taxInvoiceNo, taxInvoiceStatusLabel } from './tax-invoice'

/**
 * The commercial invoice (096) — what a shop hands a COMPANY that bought from
 * it: seller and buyer with their identification codes, the lines, the sum in
 * figures and words, and where to pay. Not the RS.GE tax invoice (that is a
 * separate service, later).
 *
 * The model is built here, pure and tested; the HTML is only its print.
 * Numbers come from the database (`issue_invoice`, one per sale, per-shop
 * sequence) and the buyer is the snapshot stored on the invoice.
 */

export type InvoiceRow = {
  id: string
  sale_id: string
  number: number
  customer_id: string | null
  buyer_name: string
  buyer_tin: string | null
  buyer_address: string | null
  created_at: string
  // 110 — the same invoice as an RS.GE tax invoice, once uploaded.
  rs_invoice_id?: number | null
  rs_series?: string | null
  rs_number?: number | null
  rs_status?: number | null
  rs_lines_ok?: boolean | null
  rs_checked_at?: string | null
}

export type InvoiceShop = {
  companyName: string
  companyId: string
  address: string
  phone: string
  bankDetails: string
  taxStatus: 'none' | 'small' | 'vat'
}

/** `unit` (1.58, a quote): printed after the quantity — 10 ც, 2 ყუთი. */
export type InvoiceLine = { name: string; qty: number; price: number; amount: number; unit?: string }

export type InvoiceModel = {
  /** 1.58 — the same paper as a quote before payment (lib/quote.ts): its own title, validity and footer */
  kind?: 'invoice' | 'quote'
  validUntil?: string
  note?: string
  number: string
  date: string
  seller: { name: string; tin: string; address: string; phone: string; bank: string }
  buyer: { name: string; tin: string; address: string }
  lines: InvoiceLine[]
  /** Σ lines — list prices less promotions, before the sale-wide discount. */
  subtotal: number
  discount: number
  bonus: number
  total: number
  /** VAT inside the total (18/118), only for a VAT payer; null otherwise. */
  vat: number | null
  /** Credit sale: what was paid at the till and what is still owed. */
  paid: number | null
  due: number | null
  words: string
  /** 110 — the RS.GE tax invoice this one was filed as, if it was. */
  rs: { no: string; status: string } | null
}

const r2 = (n: number) => Math.round(n * 100) / 100

/** INV-0001 … the number the shop's accountant will look up. */
export const invoiceNumber = (n: number) => `INV-${String(n).padStart(4, '0')}`

export function buildInvoice(
  inv: InvoiceRow, sale: Sale, items: SaleItem[], shop: InvoiceShop,
  /** 1.75 — a sale line keeps no unit: its product says. A piece prints the bare number, as ever. */
  unitOf?: (productId: string | null | undefined) => ProductUnit | null | undefined,
): InvoiceModel {
  // ₾0 lines are consumables that ride along (a cup with a coffee) — off the
  // customer's paper, exactly as on the till receipt.
  const lines = items
    .filter((i) => i.unit_price > 0)
    .map((i) => {
      const u = unitOf?.(i.product_id)
      return { name: i.product_name, qty: i.quantity, price: i.unit_price, amount: r2(i.total_price), ...(u && isMeasure(u) ? { unit: UNIT_SHORT[u] } : {}) }
    })
  const subtotal = r2(lines.reduce((s, l) => s + l.amount, 0))
  const discount = r2(sale.discount ?? 0)
  const bonus = r2(sale.bonus_redeemed ?? 0)
  const total = r2(sale.total)
  const credit = sale.payment_method === 'credit'
  const paid = credit ? r2(sale.paid ?? 0) : null
  const d = new Date(inv.created_at)
  return {
    number: invoiceNumber(inv.number),
    date: `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`,
    seller: {
      name: shop.companyName || '—', tin: shop.companyId || '—', address: shop.address || '',
      phone: shop.phone || '', bank: shop.bankDetails || '',
    },
    buyer: { name: inv.buyer_name, tin: inv.buyer_tin ?? '—', address: inv.buyer_address ?? '' },
    lines,
    subtotal,
    discount,
    bonus,
    total,
    vat: shop.taxStatus === 'vat' ? r2(vatInside(total)) : null,
    paid,
    due: credit ? r2(Math.max(0, total - (paid ?? 0))) : null,
    words: lariInWords(total),
    rs: inv.rs_invoice_id
      ? { no: taxInvoiceNo(inv.rs_series, inv.rs_number), status: taxInvoiceStatusLabel(inv.rs_status) }
      : null,
  }
}

export function invoiceHtml(m: InvoiceModel): string {
  const quote = m.kind === 'quote'
  const title = quote ? 'ფასის შეთავაზება' : 'ინვოისი'
  const rows = m.lines.map((l, i) => `<tr><td>${i + 1}</td><td class="l">${esc(l.name)}</td><td>${l.qty}${l.unit ? ` ${esc(l.unit)}` : ''}</td><td class="r">${m2(l.price)}</td><td class="r">${m2(l.amount)}</td></tr>`).join('')
  const sum = (label: string, v: string, bold = false) => `<tr${bold ? ' class="b"' : ''}><td>${esc(label)}</td><td class="r">${v}</td></tr>`
  return `<!doctype html><html lang="ka"><head><meta charset="utf-8"><title>${title} ${esc(m.number)}</title>
<style>
  @page { size: A4 portrait; margin: 14mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Noto Sans Georgian', 'Sylfaen', 'DejaVu Sans', sans-serif; color: #111; margin: 0; font-size: 12px; }
  .head { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 2px solid #111; padding-bottom: 10px; }
  .head h1 { margin: 0; font-size: 26px; letter-spacing: 1px; }
  .head .no { text-align: right; } .head .no b { font-size: 16px; }
  .parties { display: flex; gap: 14px; margin: 16px 0; }
  .party { flex: 1; border: 1px solid #bbb; border-radius: 6px; padding: 10px 12px; }
  .party .cap { font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #666; margin-bottom: 4px; }
  .party .nm { font-size: 14px; font-weight: 700; } .party div { margin-top: 2px; }
  table.lines { width: 100%; border-collapse: collapse; }
  table.lines th, table.lines td { border-bottom: 1px solid #ddd; padding: 6px 6px; text-align: center; }
  table.lines th { background: #f2f2f2; font-weight: 700; font-size: 11px; }
  table.lines td.l { text-align: left; } table.lines td.r, .totals td.r { text-align: right; white-space: nowrap; }
  .totals { display: flex; justify-content: flex-end; margin-top: 10px; }
  .totals table { border-collapse: collapse; min-width: 46%; }
  .totals td { padding: 4px 6px; } .totals tr.b td { font-weight: 800; font-size: 15px; border-top: 2px solid #111; }
  .words { margin-top: 8px; font-size: 12px; } .words b { font-weight: 700; }
  .bank { margin-top: 16px; padding: 10px 12px; border: 1px dashed #999; border-radius: 6px; }
  .sign { display: flex; gap: 40px; margin-top: 46px; } .sign div { flex: 1; border-top: 1px solid #111; padding-top: 4px; font-size: 11px; color: #444; }
</style></head><body>
<div class="head"><h1>${title}</h1><div class="no"><b>${quote ? '№ ' : ''}${esc(m.number)}</b><br>თარიღი: ${esc(m.date)}${quote && m.validUntil ? `<br><b>მოქმედებს ${esc(m.validUntil)}-მდე</b>` : ''}${m.rs ? `<br>RS.GE ანგარიშ-ფაქტურა: ${esc(m.rs.no)} · ${esc(m.rs.status)}` : ''}</div></div>
<div class="parties">
  <div class="party"><div class="cap">გამყიდველი</div><div class="nm">${esc(m.seller.name)}</div><div>ს/კ ${esc(m.seller.tin)}</div>${m.seller.address ? `<div>${esc(m.seller.address)}</div>` : ''}${m.seller.phone ? `<div>ტელ. ${esc(m.seller.phone)}</div>` : ''}</div>
  <div class="party"><div class="cap">მყიდველი</div><div class="nm">${esc(m.buyer.name)}</div><div>ს/კ ${esc(m.buyer.tin)}</div>${m.buyer.address ? `<div>${esc(m.buyer.address)}</div>` : ''}</div>
</div>
<table class="lines"><thead><tr><th>#</th><th style="text-align:left">დასახელება</th><th>რაოდენობა</th><th style="text-align:right">ფასი ₾</th><th style="text-align:right">თანხა ₾</th></tr></thead><tbody>${rows}</tbody></table>
<div class="totals"><table>
  ${m.discount > 0 || m.bonus > 0 ? sum('ჯამი', m2(m.subtotal)) : ''}
  ${m.discount > 0 ? sum('ფასდაკლება', '−' + m2(m.discount)) : ''}
  ${m.bonus > 0 ? sum('ბონუსით დაფარული', '−' + m2(m.bonus)) : ''}
  ${sum('სულ გადასახდელი', '₾' + m2(m.total), true)}
  ${m.vat != null ? sum('მათ შორის დღგ 18%', m2(m.vat)) : ''}
  ${m.paid != null ? sum('გადახდილია', m2(m.paid)) : ''}
  ${m.due != null ? sum('გადასახდელი დარჩა', m2(m.due)) : ''}
</table></div>
<div class="words">თანხა სიტყვიერად: <b>${esc(m.words)}</b></div>
${quote && m.note ? `<div class="words">შენიშვნა: ${esc(m.note)}</div>` : ''}
${m.seller.bank ? `<div class="bank"><b>საბანკო რეკვიზიტები:</b> ${esc(m.seller.bank)}<br>დანიშნულება: ${esc(m.number)}</div>` : ''}
<div class="sign"><div>გამყიდველი (ხელმოწერა)</div><div>მყიდველი (ხელმოწერა)</div></div>
${quote ? `<p style="margin-top:18px;font-size:11px;color:#444;border-top:1px solid #ccc;padding-top:8px">ეს არის ფასის შეთავაზება. ის არ არის ჩეკი და არ არის საგადასახადო ანგარიშ-ფაქტურა.${m.validUntil ? ` ფასი მოქმედებს ${esc(m.validUntil)}-მდე.` : ''}</p>` : ''}
</body></html>`
}

/** Open the print window. False when the browser blocked the pop-up. */
export function printInvoice(m: InvoiceModel): boolean {
  return printA4(invoiceHtml(m))
}

/** An A4 document in its own window, printed once its fonts are in. */
export function printA4(html: string): boolean {
  const w = window.open('', '_blank', 'width=900,height=1100')
  if (!w) return false
  w.document.open()
  w.document.write(html)
  w.document.close()
  const go = () => { w.focus(); w.print() }
  if (w.document.fonts?.ready) void w.document.fonts.ready.then(() => setTimeout(go, 150))
  else setTimeout(go, 400)
  return true
}
